const DEFAULT_MAX_QUEUED_BYTES = 32 * 1024 * 1024;

export async function createOpfsRecordingSink({
  fileName,
  workerFactory = null,
  storageRoot = null,
  maxQueuedBytes = DEFAULT_MAX_QUEUED_BYTES
} = {}) {
  if (!fileName) throw new TypeError('fileName is required');

  const createWorker = workerFactory || (() => {
    if (typeof Worker !== 'function') throw new Error('Worker is unavailable');
    return new Worker(
      new URL('./opfs-writer-worker.js', import.meta.url),
      { type: 'module', name: 'criccam-opfs-writer' }
    );
  });
  const worker = createWorker();
  const pending = new Map();
  let requestCounter = 0;
  let queuedBytes = 0;
  let bytesWritten = 0;
  let closed = false;
  let fatalError = null;

  const onMessage = event => {
    const message = event?.data || {};
    const request = pending.get(message.requestId);
    if (!request) return;
    pending.delete(message.requestId);
    queuedBytes = Math.max(0, queuedBytes - request.queuedBytes);
    if (message.type === 'failure') {
      const error = new Error(message.message || 'OPFS worker failed');
      error.code = message.code;
      request.reject(error);
      return;
    }
    if (Number.isFinite(message.totalBytes)) bytesWritten = message.totalBytes;
    request.resolve(message);
  };

  const onError = event => {
    abort(new Error(event?.message || 'OPFS worker crashed'));
  };

  worker.addEventListener('message', onMessage);
  worker.addEventListener('error', onError);

  function request(type, payload = {}, transfer = [], requestBytes = 0) {
    if (closed) return Promise.reject(fatalError || new Error('OPFS writer is closed'));
    if (queuedBytes + requestBytes > maxQueuedBytes) {
      return Promise.reject(new Error('OPFS writer queue limit exceeded'));
    }

    const requestId = ++requestCounter;
    queuedBytes += requestBytes;
    return new Promise((resolve, reject) => {
      pending.set(requestId, {
        resolve,
        reject,
        queuedBytes: requestBytes
      });
      try {
        worker.postMessage({ type, requestId, ...payload }, transfer);
      } catch (error) {
        pending.delete(requestId);
        queuedBytes = Math.max(0, queuedBytes - requestBytes);
        reject(error);
      }
    });
  }

  function abort(error = new Error('OPFS writer aborted')) {
    if (closed) return;
    fatalError = error;
    closed = true;
    for (const entry of pending.values()) entry.reject(error);
    pending.clear();
    queuedBytes = 0;
    try { worker.postMessage({ type: 'abort', requestId: ++requestCounter }); } catch (_) {}
    try { worker.terminate(); } catch (_) {}
    worker.removeEventListener?.('message', onMessage);
    worker.removeEventListener?.('error', onError);
  }

  try {
    await request('init', { fileName });
  } catch (error) {
    abort(error);
    throw error;
  }

  return {
    persistent: true,
    kind: 'opfs-sync-worker',
    async write(chunk) {
      if (!(chunk instanceof Blob)) throw new TypeError('chunk must be a Blob');
      if (queuedBytes + chunk.size > maxQueuedBytes) {
        throw new Error('OPFS writer queue limit exceeded');
      }
      queuedBytes += chunk.size;
      let buffer;
      try {
        buffer = await chunk.arrayBuffer();
      } catch (error) {
        queuedBytes = Math.max(0, queuedBytes - chunk.size);
        throw error;
      }
      queuedBytes = Math.max(0, queuedBytes - chunk.size);
      return request('write', { buffer }, [buffer], chunk.size);
    },
    flush() {
      return request('flush');
    },
    async finish() {
      await request('finish');
      closed = true;
      try { worker.terminate(); } catch (_) {}
      worker.removeEventListener?.('message', onMessage);
      worker.removeEventListener?.('error', onError);

      let root = storageRoot;
      if (!root) root = await globalThis.navigator?.storage?.getDirectory?.();
      if (!root) throw new Error('OPFS root is unavailable after finalization');
      const handle = await root.getFileHandle(fileName);
      const blob = await handle.getFile();
      return {
        blob,
        cleanup: () => root.removeEntry(fileName).catch(() => {})
      };
    },
    abort,
    getMetrics() {
      return {
        bytesWritten,
        queuedBytes,
        pendingWrites: pending.size,
        maxQueuedBytes,
        failed: !!fatalError
      };
    }
  };
}
